import React from 'react';
// Use VoyagerSearchContext for Voyager API integration
import { useVoyagerSearch as useSearch } from '../../context/VoyagerSearchContext';
import { FilterItem } from '../../models/search-result';
import './FilterPills.scss';

const FilterPills: React.FC = () => {
  const {
    searchState,
    updateSearchQuery,
    updateActiveFilter,
    applySearchParams,
  } = useSearch();

  // Get section title from filterSections (same way FilterSidebar does)
  const deriveActiveFilter = (filters: FilterItem[]): string => {
    // If no filters remain, set to 'all'
    if (filters.length === 0) {
      return 'all';
    }
    if (
      filters.some(
        (filter) =>
          filter.sectionId === 'thesaurusName' &&
          filter.id.toLowerCase().includes('ngda'),
      )
    ) {
      return 'ngda-themes';
    }
    if (
      filters.some(
        (filter) => filter.sectionId === 'type' && filter.id === 'dataset',
      )
    ) {
      return 'datasets';
    }
    if (
      filters.some(
        (filter) => filter.sectionId === 'type' && filter.id === 'service',
      )
    ) {
      return 'services';
    }
    if (
      filters.some(
        (filter) =>
          filter.sectionId === 'format' &&
          filter.id.toLowerCase().includes('geoplatform'),
      )
    ) {
      return 'cached-services';
    }
    return 'all';
  };

  const handleRemoveSearchTerm = () => {
    updateSearchQuery('');
    applySearchParams({ search: '', pageIndex: 0 });
  };

  const handleRemoveFilter = (filterToRemove: any) => {
    const updatedFilters =
      searchState.query?.filters?.filter(
        (filter) => filter.id !== filterToRemove.id,
      ) || [];
    updateActiveFilter(deriveActiveFilter(updatedFilters as FilterItem[]));
    applySearchParams({ filters: updatedFilters, pageIndex: 0 });
  };

  const handleRemovePlace = () => {
    // Remove place filter and clear the bbox
    // Use null to explicitly signal removal (undefined means "use existing")
    applySearchParams({
      place: null,
      locationQuery: '',
      bbox: [],
      pageIndex: 0,
    });
  };

  const searchTerm = searchState.query?.search;
  const activeFilters = searchState.query?.filters || [];
  const place = searchState.query?.place;

  // Don't render if no search term, filters, or place
  if (!searchTerm && activeFilters.length === 0 && !place) {
    return null;
  }

  return (
    <div className="filter-pills-container">
      {/* Search term pill */}
      {searchTerm && (
        <div className="filter-pill search-pill">
          <span className="pill-text">"{searchTerm}"</span>
          <button
            className="pill-remove-btn"
            onClick={handleRemoveSearchTerm}
            aria-label={`Remove search term: ${searchTerm}`}
          >
            ✕
          </button>
        </div>
      )}

      {/* Place/location pill */}
      {place && place.name && (
        <div className="filter-pill place-pill">
          <span className="pill-text">{place.name}</span>
          <button
            className="pill-remove-btn"
            onClick={handleRemovePlace}
            aria-label={`Remove location filter: ${place.name}`}
          >
            ✕
          </button>
        </div>
      )}

      {/* Filter pills */}
      {activeFilters.map((filter, index) => (
        <div
          key={`${filter.sectionId}-${filter.id}-${index}`}
          className="filter-pill"
        >
          <span className="pill-text">{filter.label}</span>
          <button
            className="pill-remove-btn"
            onClick={() => handleRemoveFilter(filter)}
            aria-label={`Remove filter: ${filter.label}`}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
};

export default FilterPills;
